import { localization } from '@/plugins/i18n'
import type { Locale } from 'vue-i18n'
import { DateFormat, ds, ts } from '@/plugins/i18nFormat'
import { getBase64ImageFromURL } from '@/utils/assets'
import csjLogo from '@/assets/csjlogo.jpg'
import { version as appVersion } from '@/../package.json'

export interface PrintMetaParams {
    event: any
    title: string
    pageOrientation?: 'portrait' | 'landscape'
    locale?: Locale
}

export async function buildPrintMeta(params: PrintMetaParams) {
    const locale = params.locale
    const ev = params.event
    const author = ev?.clubTitleShort
    const subject = (ev?.ctlId ?? '') + ' ' + (ev?.title ?? '')

    const csjLogoBase64 = await getBase64ImageFromURL(csjLogo)

    return {
        pageSize: 'A4',
        pageOrientation: params.pageOrientation ?? 'portrait',
        pageMargins: [20, 45, 20, 30] as [number, number, number, number],
        defaultStyle: {
            font: 'Roboto',
            fontSize: 10,
            opacity: .6,
        },
        version: '1.5',
        subset: 'PDF/A-3b',
        displayTitle: true,
        info: {
            title: params.title,
            author: author,
            subject: subject,
            creator: ts('appName', {}, { locale }) + ' ' + ts('version', { version: appVersion }, { locale }),
            producer: author,
            language: (locale ?? localization.global.locale.value) === 'en' ? 'en-US' : 'cs-CZ',
        },
        header: (currentPage: number) => [
            {
                marginLeft: 20,
                marginRight: 20,
                marginTop: 10,
                table: {
                    headerRows: 0,
                    widths: ['auto', '*', 'auto'] as any,
                    body: [
                        [
                            { image: csjLogoBase64, height: 24, width: 21 },
                            { text: params.title, style: 'h1', marginTop: 5 },
                            {
                                marginTop: 11,
                                text: ts('pageNo', { pageNo: currentPage }, { locale }),
                                alignment: 'right',
                            },
                        ],
                    ],
                },
                layout: {
                    vLineWidth: () => (0),
                    hLineWidth: (i, node) => (i === node.table.body.length),
                    hLineColor: 'black',
                },
            },
        ],
        footer: () => [
            {
                marginLeft: 20,
                marginRight: 20,
                marginBottom: 11,
                table: {
                    headerRows: 0,
                    widths: ['*'] as any,
                    body: [
                        [
                            {
                                text: ts('appName', {}, { locale }) + ' '
                                    + ts('version', { version: appVersion }, { locale }),
                                alignment: 'right',
                                style: 'app',
                            },
                        ],
                    ],
                },
                layout: {
                    vLineWidth: () => (0),
                    hLineWidth: () => (0),
                },
            },
        ],
        styles: {
            h1: { fontSize: 16, bold: true, opacity: 1 },
            common: {},
            em: { bold: true, opacity: 1 },
            results: { fontSize: 9 },
            compact: { fontSize: 8, marginTop: 1 },
            legend: { fontSize: 8 },
            app: { fontSize: 7 },
            auto_result: { italics: true },
            em_race_result: { bold: true, opacity: 1, fontSize: 8 },
            crewlist: { fontSize: 9 },
            smaller: { fontSize: 8, marginTop: 1 },
            sign: {},
        },
    }
}

export function headerField(
    label1Key: string,
    value1: any,
    label2Key: string | null = null,
    value2: any | null = null,
    options: { locale?: Locale } = {},
) {
    // Builds a PDFMake text object with styled label/value pairs, translating labels via ts().
    // Behavior:
    // - Always emits the first pair: ts(label1Key) (style 'em') followed by value1 (style 'common').
    // - If label2Key and value2 are both non-null, emits two spaces before ts(label2Key), and DOES NOT add a colon after the first label.
    // - If label2Key is null, automatically appends a colon and two spaces after ts(label1Key). Callers pass clean keys (e.g., 'event') without punctuation or spaces.
    //   This makes call sites concise: headerField('event', value) or headerField('eventStart', v1, 'eventUntil', v2).

    const textParts: any[] = []

    const firstLabel = label2Key === null ? ts(label1Key, {}, options) + ':  ' : ts(label1Key, {}, options) + '  '
    textParts.push({ text: firstLabel, style: 'em' })
    textParts.push({ text: value1, style: 'common' })

    if (label2Key !== null && value2 !== null) {
        textParts.push({ text: '  ' + ts(label2Key, {}, options) + '  ', style: 'em' })
        textParts.push({ text: value2, style: 'common' })
    }

    return { text: textParts }
}

export function buildStandardHeader(event: any, localeOrOptions?: Locale | { locale?: Locale }) {
    const options = typeof localeOrOptions === 'string' ? { locale: localeOrOptions } : (localeOrOptions ?? {})
    const locale = options.locale
    return {
        marginTop: 3,
        table: {
            headerRows: 0,
            widths: ['*', 'auto'] as any,
            body: [
                [
                    headerField('event', event.title?.shorten(50), null, null, options),
                    headerField(
                        'eventStart',
                        ds(new Date(event.from), DateFormat.EventTo, false, locale),
                        'eventUntil',
                        ds(new Date(event.to), DateFormat.EventTo, false, locale),
                        options,
                    ),
                ],
                [
                    headerField('ctlBare', event.ctlId, null, null, options),
                    headerField('mainReferee', event.mainReferee?.reverseFullNameWithId, null, null, options),
                ],
                [
                    headerField('eventOrganizer', event.clubTitleShort?.shorten(50), null, null, options),
                    headerField('venue', event.venueTitle?.shorten(50), null, null, options),
                ],
                [
                    headerField('director', event.prinatableDirectorName?.shorten(50), null, null, options),
                    headerField('sponsor', event.sponsor?.shorten(50), null, null, options),
                ],
            ],
        },
        layout: {
            vLineWidth: () => (0),
            hLineWidth: () => (0),
        },
    }
}
